import { Box } from '@chronosphereio/chrono-ui';
import type {
  EChartsCoreOption,
  GridComponentOption,
  LegendComponentOption,
  TooltipComponentOption,
  YAXisComponentOption,
} from 'echarts';
import { LineChart as EChartsLineChart } from 'echarts/charts';
import {
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  MarkAreaComponent,
  MarkLineComponent,
  MarkPointComponent,
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
} from 'echarts/components';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { forwardRef, MouseEvent, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useUserTimeFormat } from '@/plugin-system/runtime/useUserTimeFormat';
import { ContentDimensions, UnitOptions } from '../../core';
import { useChartsTheme } from '../context/ChartsProvider';
import { useTimeZone } from '../context/TimeZoneProvider';
import { ChartApi, EChart, OnBatchEventFunction } from '../EChart';
import { ChartInstance, ChartInstanceFocusOpts, EChartsDataFormat } from '../model/graph';
import { CursorCoordinates } from '../PanelTooltip';
import { LegacyTooltipConfig, LineChartTooltip, TooltipConfig } from '../TimeSeriesTooltip';
import {
  clearHighlightedSeries,
  enableDataZoom,
  getDateRange,
  getFormattedAxis,
  legacyGetFormattedDate,
  restoreChart,
  ZoomEventData,
} from '../utils';

// eslint-disable-next-line react-hooks/rules-of-hooks
use([
  EChartsLineChart,
  GridComponent,
  DataZoomComponent,
  MarkAreaComponent,
  MarkLineComponent,
  MarkPointComponent,
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  LegendComponent,
  CanvasRenderer,
]);

const LEGACY_DEFAULT_TOOLTIP_CONFIG: LegacyTooltipConfig = {
  mode: 'nearby',
  wrapSeriesName: false,
  enablePinning: true,
};

export interface LineChartBaseProps extends ContentDimensions {
  data: EChartsDataFormat;
  yAxis?: YAXisComponentOption;
  unit?: UnitOptions;
  grid?: GridComponentOption;
  legend?: LegendComponentOption;
  tooltipConfig?: TooltipConfig;
  noDataVariant?: 'chart' | 'message';
  syncGroup?: string;
  onDataZoom?: (e: ZoomEventData) => void;
  onDoubleClick?: (e: MouseEvent) => void;
}

/**
 * Use TimeChartBase.tsx instead for improved performance and xAxis labels bucketed to more intuitive intervals.
 * @deprecated
 */
export const LineChartBase = forwardRef<ChartInstance, LineChartBaseProps>(function LineChartBase(
  {
    width,
    height,
    data,
    yAxis,
    unit,
    grid,
    legend,
    tooltipConfig = LEGACY_DEFAULT_TOOLTIP_CONFIG,
    noDataVariant = 'message',
    syncGroup,
    onDataZoom,
    onDoubleClick,
  },
  ref
) {
  const chartsTheme = useChartsTheme();
  const chartApiRef = useRef<ChartApi>();
  const [showTooltip, setShowTooltip] = useState<boolean>(true);
  const [tooltipPinnedCoords, setTooltipPinnedCoords] = useState<CursorCoordinates | null>(null);
  const { timeZone } = useTimeZone();
  const timeFormat = useUserTimeFormat();

  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);

  useImperativeHandle(ref, () => {
    return {
      highlightSeries({ id }: ChartInstanceFocusOpts) {
        if (!chartApiRef.current) {
          // when chart undef, do not highlight series when hovering over legend
          return;
        }

        chartApiRef.current.dispatchAction({ type: 'highlight', seriesId: id });
      },
      clearHighlightedSeries: () => {
        if (!chartApiRef.current) {
          // when chart undef, do not clear highlight series
          return;
        }
        clearHighlightedSeries(chartApiRef.current);
      },
    };
  }, []);

  const handleDataZoom: OnBatchEventFunction = useCallback(
    (params) => {
      if (onDataZoom === undefined) {
        setTimeout(() => {
          // workaround so unpin happens after click event
          setTooltipPinnedCoords(null);
        }, 10);
      }
      if (onDataZoom === undefined || params.batch[0] === undefined) return;
      const startIndex = params.batch[0].startValue ?? 0;
      const endIndex = params.batch[0].endValue ?? data.xAxis.length - 1;
      const xAxisStartValue = data.xAxis[startIndex];
      const xAxisEndValue = data.xAxis[endIndex];

      if (xAxisStartValue !== undefined && xAxisEndValue !== undefined) {
        const zoomEvent: ZoomEventData = {
          start: xAxisStartValue,
          end: xAxisEndValue,
          startIndex,
          endIndex,
        };
        onDataZoom(zoomEvent);
      }
    },
    [data.xAxis, onDataZoom]
  );

  if (chartApiRef.current !== undefined) {
    enableDataZoom(chartApiRef.current);
  }

  const { noDataOption } = chartsTheme;

  const option: EChartsCoreOption = useMemo(() => {
    if (data.timeSeries === undefined) return {};

    // The "chart" `noDataVariant` is only used when the `timeSeries` is an
    // empty array because a `null` value will throw an error.
    if (data.timeSeries === null || (data.timeSeries.length === 0 && noDataVariant === 'message')) return noDataOption;

    const rangeMs = data.rangeMs ?? getDateRange(data.xAxis);

    const option: EChartsCoreOption = {
      series: data.timeSeries,
      xAxis: {
        type: 'category',
        data: data.xAxis,
        max: data.xAxisMax,
        axisLabel: {
          formatter: (label: string) => {
            return legacyGetFormattedDate({ label, rangeMs, timeFormat, timeZone });
          },
        },
      },
      yAxis: getFormattedAxis(yAxis, unit),
      animation: false,
      tooltip: {
        show: true,
        trigger: 'axis',
        showContent: false, // echarts tooltip content hidden since we use custom tooltip instead
      },
      // https://echarts.apache.org/en/option.html#axisPointer
      axisPointer: {
        type: 'line',
        z: 0, // ensure point symbol shows on top of dashed line
        triggerEmphasis: false, // https://github.com/apache/echarts/issues/18495
        triggerTooltip: false,
        snap: true,
      },
      toolbox: {
        feature: {
          dataZoom: {
            icon: null, // https://stackoverflow.com/a/67684076/17575201
            yAxisIndex: 'none',
          },
        },
      },
      grid,
      legend,
    };

    return option;
  }, [data, yAxis, unit, grid, legend, noDataOption, timeFormat, timeZone, noDataVariant]);

  return (
    <Box
      sx={{ height, width }}
      onClick={(e) => {
        // Pin and unpin when clicking on chart canvas but not tooltip text.
        if (tooltipConfig.enablePinning && e.target instanceof HTMLCanvasElement) {
          setTooltipPinnedCoords((current) => {
            if (current === null) {
              return {
                page: {
                  x: e.pageX,
                  y: e.pageY,
                },
                client: {
                  x: e.clientX,
                  y: e.clientY,
                },
                plotCanvas: {
                  x: e.nativeEvent.offsetX,
                  y: e.nativeEvent.offsetY,
                },
                target: e.target,
              };
            } else {
              return null;
            }
          });
        }
      }}
      onMouseDown={(e) => {
        const { clientX } = e;
        setIsDragging(true);
        setStartX(clientX);
      }}
      onMouseMove={(e) => {
        // Allow clicking inside tooltip to copy labels.
        if (!(e.target instanceof HTMLCanvasElement)) {
          return;
        }
        const { clientX } = e;
        if (isDragging) {
          const deltaX = clientX - startX;
          if (deltaX > 0) {
            // Hide tooltip when user drags to zoom.
            setShowTooltip(false);
          }
        }
      }}
      onMouseUp={() => {
        setIsDragging(false);
        setStartX(0);
        setShowTooltip(true);
      }}
      onMouseLeave={() => {
        if (tooltipPinnedCoords === null) {
          setShowTooltip(false);
        }
        if (chartApiRef.current !== undefined) {
          clearHighlightedSeries(chartApiRef.current);
        }
      }}
      onMouseEnter={() => {
        setShowTooltip(true);
        if (chartApiRef.current !== undefined) {
          enableDataZoom(chartApiRef.current);
        }
      }}
      onDoubleClick={(e) => {
        setTooltipPinnedCoords(null);
        // either dispatch ECharts restore action to return to orig state or allow consumer to define behavior
        if (onDoubleClick === undefined) {
          if (chartApiRef.current !== undefined) {
            restoreChart(chartApiRef.current);
          }
        } else {
          onDoubleClick(e);
        }
      }}
    >
      {/* Allows overrides prop to hide custom tooltip and use the ECharts option.tooltip instead */}
      {showTooltip === true &&
        (option.tooltip as TooltipComponentOption)?.showContent === false &&
        tooltipConfig.hidden !== true &&
        chartApiRef.current && (
          <LineChartTooltip
            chartApi={chartApiRef.current}
            chartData={data}
            tooltipConfig={tooltipConfig}
            enablePinning={tooltipConfig.enablePinning}
            pinnedPos={tooltipPinnedCoords}
            unit={unit}
            onUnpinClick={() => {
              setTooltipPinnedCoords(null);
            }}
            containerId={chartsTheme.tooltipPortalContainerId}
          />
        )}
      <EChart
        width={width}
        height={height}
        option={option}
        theme={chartsTheme.echartsTheme}
        syncGroup={syncGroup}
        chartApiRef={chartApiRef}
        onDataZoom={handleDataZoom}
      />
    </Box>
  );
});
